/* eslint-disable @typescript-eslint/no-unused-vars */
import { BladeBaseProps, DynamicContent } from '@commblades/commblades';
import { useEffect, useRef, useState } from 'react';

import {
    ButtonActionEmphasis, ButtonActionSubtle, CarouselControl, Dropdown,
} from '@honeycomb-npm/honeycomb-react';

import { useAppDispatch, useAppSelector } from '../../../../stories/hooks';
import AmountSlider from '../../../../components/AmountSlider';
import {
    selectLoanProductName, selectLoanPurposeId, selectReferenceData, selectSubLoanPurposeId,
} from '../../../netBank/netBankSelectors';
import calculateLoanRepayments, { getLoanTypeById, LoanDetails, LoanRepayments } from '../../../../utils/CalculateRepayment/loanRepayments';
import { selectBorrowingFinanceData, selectBorrowingPower } from './borrowingPowerSelectors';
import InSufficientPower from '../../../../components/InSufficientBorrowingPower';
import { LoanPresentation } from '../../../../components/LoanPresentation';
import {
    LoanProductData, LoanProductRepayment, loanTermOption,
} from './borrowingPowerTypes';

import {
    DropdownComponentWrapper, DropdownWrapper, DynamicWidthWrapper, InsufficientPowerButtonWrapper,
} from './BorrowingPower.styled';
import {
    DYNAMIC_MESSAGE_LESS_THAN_MINIMUM,
    FeesAndCharges,
    LOAN_PURPOSE_ID,
    LOAN_TYPE_ID,
    LOAN_TYPE_NAME,
    MONTHS_IN_A_YEAR,
    SECURED_LOAN_PRODUCT_INTEREST_TITLE,
    UNSECURED_LOAN_PRODUCT_FIXED_INTEREST_TITLE,
    UNSECURED_LOAN_PRODUCT_VARIABLE_INTEREST_TITLE,
} from '../../types';
import formatCurrency from '../../../../utils/formatCurrency';
import { selectProductType } from '../yourFinance/yourFinanceSelectors';
import { removeExistingBladesAfter, startBladeNavigation } from '../../navigationSlice';
import {
    updateFeesAndCharges, updateLoanTerm, updateLoanTypeName, updateStandardRate, updateTotalLoanAmount,
} from '../../../netBank/netBankSlice';
import { BorrowingAmountWithLoanTerms } from '../yourFinance/yourFinanceTypes';
import getProductID from './borrowingPowerApi';

const BorrowingPower = ({ isFocusedBlade }: BladeBaseProps) => {
    const {
        loanYearsForm,
        borrowingCapacityLabel,
        heading,
        description,
        normalInterest,
        greenInterest,
        greenDescription,
        interestDescription,
        representativeRateInfo,
        offerInfo,
        exploreLoanOptionsButtonText,
        loanTermsDropDownLabel,
        resetEstimateText,
        loanOptions,
        peronalLoansLink,
    } = useAppSelector(selectBorrowingPower);

    const financeData = useAppSelector(selectBorrowingFinanceData);
    const { feesAndCharges, loanConfiguration } = useAppSelector(selectReferenceData);
    const productType = useAppSelector(selectProductType);
    const productName = useAppSelector(selectLoanProductName);
    const selectedLoanPurposeId = useAppSelector(selectLoanPurposeId);
    const selectedSubLoanPurposeId = useAppSelector(selectSubLoanPurposeId);

    const dispatch = useAppDispatch();
    const [actualHeading, setActualHeading] = useState('');
    const [borrowingAmount, setBorrowingAmount] = useState(0);
    const [maxAmount, setMaxAmount] = useState(0);
    const [isError, setIsError] = useState(false);
    const [isCar, setIsCar] = useState(false);
    const [isCarOlderThan7Years, setIsCarOlderThan7Years] = useState(false);
    const [isHiddenAllDynamicMessages, setIsHiddenAllDynamicMessages] = useState(false);
    const [inSufficentInterestParas, setInSufficentInterestParas] = useState<string[]>([]);
    const [interestParas, setInterestParas] = useState<string[]>([]);
    const [insufficientBorrowingAmount, setInsufficientBorrowingAmount] = useState(0);
    const [showInsufficientPower, setShowInsufficientPower] = useState(false);
    const [showInsufficientPowerDropdown, setShowInsufficientPowerDropdown] = useState(false);
    const [cappedFeesAndCharges, setCappedFeesAndCharges] = useState<FeesAndCharges[]>([]);
    const [products, setProducts] = useState<LoanProductData[]>([]);
    const [loanTerms, setLoanTerms] = useState<loanTermOption>({ label: '7', value: '7' });
    const [repayments, setRepayments] = useState<LoanRepayments[]>([]);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const slideInputRef = useRef<HTMLInputElement>(null);
    const excludeLoanPurposesFromSPL = [
        LOAN_PURPOSE_ID.CAR,
        LOAN_PURPOSE_ID.GREEN_PURCHASES_ELECTRIC_HYBRID,
    ] as Array<string | number>;

    const isNotCarLoan = !(excludeLoanPurposesFromSPL.includes(selectedSubLoanPurposeId)
          || excludeLoanPurposesFromSPL.includes(selectedLoanPurposeId));

    const handleAmountChange = (value: number) => {
        setBorrowingAmount(value);
    };

    const onSliderError = (error: string) => {
        if (error !== '') {
            setIsError(true);
        } else {
            setIsError(false);
        }
    };

    const handleNextButton = () => {
        if (isError) {
            slideInputRef.current?.focus();
        } else {
            window.open(peronalLoansLink, '_blank')?.focus();
        }
    };

    const handleResetButton = () => {
        dispatch(startBladeNavigation('ApplicationStart'));
        dispatch(removeExistingBladesAfter('ApplicationStart'));
    };

    const handleYearsChange = (name: string, value: string) => {
        const selectedLoanYears = loanYearsForm.DropdownOptions.filter((o) => o.value === value);

        if (selectedLoanYears.length > 0) {
            setLoanTerms({ label: selectedLoanYears[0].key, value: selectedLoanYears[0].value });
            dispatch(updateLoanTerm(Number(selectedLoanYears[0].value)));
        }
    };

    const getProductAEMByKeyName = (loanType: string, keyName: string): string => {
        switch (loanType) {
        case 'SecuredLoan':
            return loanOptions?.secured?.[keyName as keyof typeof loanOptions.secured] || 'Unknown';
        case 'FixedRate':
            return loanOptions?.fixed?.[keyName as keyof typeof loanOptions.fixed] || 'Unknown';
        case 'VariableRate':
            return loanOptions?.variable?.[keyName as keyof typeof loanOptions.variable] || 'Unknown';
        default:
            return 'Unknown';
        }
    };

    const getCappedAmountBasedOnProduct = (loanType: string): number => {
        switch (loanType) {
        case 'SecuredLoan':
            return loanConfiguration.maximumLoanAmount;
        case 'FixedRate':
            return loanConfiguration.maximumUnsecuredLoanAmount;
        case 'VariableRate':
            return loanConfiguration.maximumUnsecuredLoanAmount;
        default:
            return loanConfiguration.maximumUnsecuredLoanAmount;
        }
    };

    const getCappedAmountBasedOnTerms = (
        borrowingAmountWithLoanTerms: BorrowingAmountWithLoanTerms,
        loanTerm: number,
        loanType: string,
    ): number => {
        if (borrowingAmountWithLoanTerms) {
            if (Object.prototype.hasOwnProperty.call(borrowingAmountWithLoanTerms, loanTerm)) {
                // set the insufficient borrowing amount if the loan term is not valid
                if (!borrowingAmountWithLoanTerms[loanTerm as keyof typeof borrowingAmountWithLoanTerms].isValid
                    && loanType === LOAN_TYPE_NAME.SECURED) {
                    setInsufficientBorrowingAmount(borrowingAmountWithLoanTerms[loanTerm as keyof typeof borrowingAmountWithLoanTerms].amount);
                }

                return borrowingAmountWithLoanTerms[loanTerm as keyof typeof borrowingAmountWithLoanTerms].amount;
            }

            return borrowingAmountWithLoanTerms[1 as keyof typeof borrowingAmountWithLoanTerms].amount;
        }

        return 0;
    };

    const getDymaticProductCappedAmount = (loanTypeId: number): number => {
        const matchedLoan = financeData.estimation.find((e) => e.loanType === getLoanTypeById(loanTypeId));

        if (matchedLoan) {
            const { borrowingAmountWithLoanTerms } = matchedLoan;
            const cappedTermAmount = getCappedAmountBasedOnTerms(borrowingAmountWithLoanTerms, Number(loanTerms.value), getLoanTypeById(loanTypeId));

            return Math.min(matchedLoan.borrowAmount, cappedTermAmount);
        }

        return 0;
    };

    const getProductInterestTitle = (loanType:string) => {
        switch (loanType) {
        case LOAN_TYPE_NAME.SECURED:
            return SECURED_LOAN_PRODUCT_INTEREST_TITLE;
        case LOAN_TYPE_NAME.UNSECURED_FIXED:
            return UNSECURED_LOAN_PRODUCT_FIXED_INTEREST_TITLE;
        case LOAN_TYPE_NAME.UNSECURED_VARIABLE:
            return UNSECURED_LOAN_PRODUCT_VARIABLE_INTEREST_TITLE;
        default:
            return UNSECURED_LOAN_PRODUCT_VARIABLE_INTEREST_TITLE;
        }
    };

    const getDynamicMessageInfo = (loanType:string, borrowingAmountWithLoanTerms: BorrowingAmountWithLoanTerms) => {
        let realDynamicMessage = getProductAEMByKeyName(loanType, 'borrowingCapacityTooltip');

        if (borrowingAmountWithLoanTerms) {
            const loanTermObj = borrowingAmountWithLoanTerms[loanTerms.value as unknown as keyof typeof borrowingAmountWithLoanTerms];

            if (loanTermObj && loanTermObj.amount < loanConfiguration.minimumLoanAmount) {
                realDynamicMessage = DYNAMIC_MESSAGE_LESS_THAN_MINIMUM;
            }
        }
        const repayment = repayments.find((r) => r.loanType === loanType);

        if (repayment?.cappedAmount) {
            if (borrowingAmount > repayment?.cappedAmount) {
                return {
                    message: realDynamicMessage,
                    isHidden: false,
                };
            }

            return {
                message: realDynamicMessage,
                isHidden: true,
            };
        }

        return undefined;
    };

    const getRepaymentByLoanType = (
        loanType:string,
        interestRate: number,
        comparisionRate: number,
        borrowingAmountWithLoanTerms: BorrowingAmountWithLoanTerms,
        isStandard: boolean,
    ) => {
        const repayment = repayments.find((r) => r.loanType === loanType);
        const cappedAmount = loanType.indexOf('Secured') > -1
            ? loanConfiguration.maximumLoanAmount : loanConfiguration.maximumUnsecuredLoanAmount;
        const realToolTip = getProductAEMByKeyName(loanType, 'amountToolTip')
            .replace('{0}', `$${formatCurrency(cappedAmount)}`);
        const realBorrowingAmount = repayment?.cappedAmount ? Math.min(borrowingAmount, repayment.cappedAmount) : borrowingAmount;

        let realRepaymentAmount = '';
        const displayRealBorrowingAmount = realBorrowingAmount < loanConfiguration.minimumLoanAmount
            ? '--'
            : `([$${formatCurrency(realBorrowingAmount)}]{${realToolTip}})`;

        if (repayment?.amount) {
            realRepaymentAmount = `$${repayment.amount}`;
        }
        // If the loan amount is less than the minimum loan amount for the selected term, then the repayment amount should be undefined
        if (borrowingAmountWithLoanTerms) {
            const loanTermObj = borrowingAmountWithLoanTerms[loanTerms.value as unknown as keyof typeof borrowingAmountWithLoanTerms];

            if (loanTermObj && loanTermObj.amount < loanConfiguration.minimumLoanAmount) {
                realRepaymentAmount = '--';
            }
        }

        let desc = getProductAEMByKeyName(loanType, 'description');

        if (loanType.indexOf('Secured') > -1 && isCar) {
            desc = getProductAEMByKeyName(loanType, 'evDescription');
        }

        const bottomDes = isStandard ? getProductAEMByKeyName(loanType, 'interestComparisonText')
            : getProductAEMByKeyName(loanType, 'greenDiscount');

        if (repayment) {
            return {
                heading: LoanProductRepayment.heading,
                description:
                `${desc}<hr aria-hidden="true"/>${getProductAEMByKeyName(loanType, 'repayment')
                    .replace('{0}', displayRealBorrowingAmount)
                    .replace('{1}', `${loanTerms.label}`)
                    .replace('{2}', 'weekly')
                    .replace('{3}', `${realRepaymentAmount}`)
                }`,
                amount: 0,
                frequency: loanYearsForm.DropdownOptions.find((o) => o.value === loanTerms.value)?.key || '',
                bottomDescription: bottomDes
                    .replace('{0}', `${formatCurrency(interestRate, true).toString()}`)
                    .replace('{1}', `${formatCurrency(comparisionRate, true).toString()}`),
            };
        }

        return {
            heading: LoanProductRepayment.heading,
            description: LoanProductRepayment.description
                .replace('{0}', `$${formatCurrency(realBorrowingAmount)}`)
                .replace('{1}', `${loanTerms.label}`),
            amount: 0,
            frequency: '',
            bottomDescription: LoanProductRepayment.bottomDescription
                .replace('{0}', `${formatCurrency(interestRate, true).toString()}%`)
                .replace('{1}', `${formatCurrency(comparisionRate, true).toString()}%`),
        };
    };

    const checkShowInsufficientPower = () => {
        let isShowInsufficentPower = true;
        let isShowInsufficentDropDown = true;

        financeData.estimation.forEach(({ borrowingAmountWithLoanTerms }) => {
            if (borrowingAmountWithLoanTerms) {
                const loanTermObj = borrowingAmountWithLoanTerms[loanTerms.value as unknown as keyof typeof borrowingAmountWithLoanTerms];

                if (loanTermObj) {
                    isShowInsufficentPower = isShowInsufficentPower && !loanTermObj.isValid;
                    isShowInsufficentDropDown = isShowInsufficentPower;
                }
            } else {
                isShowInsufficentDropDown = false;
            }
        });

        setShowInsufficientPower(isShowInsufficentPower);
        setShowInsufficientPowerDropdown(isShowInsufficentDropDown);
    };

    const calculateCappedFees = () => {
        let cappedFees = feesAndCharges
            .filter((f) => f.loanType === productType)
            .map((curr) => ({
                interestRate: curr.interestRate,
                establishmentFee: curr.establishmentFee,
                monthlyServiceFee: curr.monthlyServiceFee,
                loanType: curr.loanType,
                loanTypeId: curr.loanTypeId,
                cappedAmount: getDymaticProductCappedAmount(curr.loanTypeId),
            }));

        if (isNotCarLoan) {
            cappedFees = cappedFees.filter((item) => item.loanTypeId !== LOAN_TYPE_ID.SECURED);
        }
        const maxCappedAmount = cappedFees.reduce((acc, curr) => Math.max(acc, curr.cappedAmount ?? 0), 0);

        setBorrowingAmount(maxCappedAmount);

        setMaxAmount(maxCappedAmount);

        setCappedFeesAndCharges(cappedFees);
    };

    const calculateRepayment = () => {
        if (loanTerms.value !== '') {
            const loanDetails: LoanDetails = {
                amount: borrowingAmount,
                repaymentFrequency: 'weekly',
                loanTermsInMonth: Number(loanTerms.value) * MONTHS_IN_A_YEAR,
            };

            const loanRepayments: LoanRepayments[] = calculateLoanRepayments(loanDetails, cappedFeesAndCharges);

            setRepayments(loanRepayments);
        }
    };

    const setDynamicMessageHidden = () => {
        const isHiddenAllDynamic = products.every((p) => {
            if (p.dynamicMessageInfo) return p.dynamicMessageInfo.isHidden;

            return true;
        });

        setIsHiddenAllDynamicMessages(isHiddenAllDynamic);
    };

    useEffect(() => {
        setActualHeading(heading.replace('{0}', productName));
        if (selectedLoanPurposeId === LOAN_PURPOSE_ID.CAR
            && (selectedSubLoanPurposeId === LOAN_PURPOSE_ID.USED_ELECTRIC_HYBRID_MORE_THAN_7_YEARS
                || selectedSubLoanPurposeId === LOAN_PURPOSE_ID.USED_PETROL_OR_DIESEL_MORE_THAN_7_YEARS)) {
            setIsCarOlderThan7Years(true);
        } else {
            setIsCarOlderThan7Years(false);
        }

        if (selectedLoanPurposeId === LOAN_PURPOSE_ID.CAR
            || selectedSubLoanPurposeId === LOAN_PURPOSE_ID.GREEN_PURCHASES_ELECTRIC_HYBRID) {
            setIsCar(true);
        } else {
            setIsCar(false);
        }
    }, []);

    useEffect(() => {
        setDynamicMessageHidden();
    }, [products]);

    useEffect(() => {
        calculateRepayment();
        dispatch(removeExistingBladesAfter('BorrowingPower'));
        dispatch(updateTotalLoanAmount(borrowingAmount));
    }, [loanTerms, borrowingAmount]);

    useEffect(() => {
        checkShowInsufficientPower();
        calculateCappedFees();
    }, [loanTerms]);

    useEffect(() => {
        let newEstimation = financeData.estimation.map((e) => ({
            ...e,
            loanTypeId: getProductID(e.loanType),
        }));

        if (isNotCarLoan) {
            newEstimation = newEstimation.filter((item) => item.loanTypeId !== LOAN_TYPE_ID.SECURED);
        }
        const arr = [...newEstimation].sort((a, b) => b.loanTypeId - a.loanTypeId);

        // if the car is more than 7 years, then move the car loan to the last
        if (isCarOlderThan7Years) {
            const shiftedItem = arr.shift();

            if (shiftedItem) {
                arr.push(shiftedItem);
            }
        }

        const currProducts = arr.reduce((acc, curr, idx: number) => [
            ...acc,
            {
                isRecommendedPrimary: true,
                isRecommendedPrimaryText: getProductAEMByKeyName(curr.loanType, 'recommendedText'),
                isRecommendedSecondary: false,
                isRecommendedSecondaryText: getProductAEMByKeyName(curr.loanType, 'recommendedText'),
                isRecommendedOffer: getProductAEMByKeyName(curr.loanType, 'isRecommendedOffer') === 'true',
                isValid: true,
                id: idx,
                heading: getProductAEMByKeyName(curr.loanType, 'heading'),
                cappedMessage: (curr.loanType !== 'SecuredLoan' && getCappedAmountBasedOnProduct(curr.loanType)
                <= getDymaticProductCappedAmount(curr.loanTypeId))
                    ? loanOptions.borrowingPowerCappedInfo : loanOptions.borrowingPowerInfo,
                repayment: getRepaymentByLoanType(
                    curr.loanType,
                    curr.interestRate,
                    curr.comparisionRate,
                    curr.borrowingAmountWithLoanTerms,
                    productType === 'Standard',
                ),
                activeInterestName: '',
                features: {
                    heading: '',
                    description: [],
                },
                offer: productType !== 'Standard' ? {
                    heading: getProductAEMByKeyName(curr.loanType, 'greenDiscountInfo'),
                    description: [],
                } : undefined,
                dynamicMessageInfo: getDynamicMessageInfo(curr.loanType, curr.borrowingAmountWithLoanTerms),
                params: {
                    cappedMessage: getCappedAmountBasedOnTerms(
                        curr.borrowingAmountWithLoanTerms,
                        Number(loanTerms.value),
                        curr.loanType,
                    ) < loanConfiguration.minimumLoanAmount
                        ? ['--'] : [`<b>$${formatCurrency(
                            getCappedAmountBasedOnTerms(curr.borrowingAmountWithLoanTerms, Number(loanTerms.value), curr.loanType),
                        )}</b>`],
                    repaymentHeading: [''],
                    repaymentDescription: [''],
                    dynamicMessage: [`$${formatCurrency(Math.max(
                        getCappedAmountBasedOnTerms(curr.borrowingAmountWithLoanTerms, Number(loanTerms.value), curr.loanType),
                        loanConfiguration.minimumLoanAmount,
                    ))}`],
                },
                hyperLinkText: getProductAEMByKeyName(curr.loanType, 'learnMoreText'),
                hyperOpenLink: getProductAEMByKeyName(curr.loanType, 'learnMoreLink'),
            } as LoanProductData,
        ], [] as LoanProductData[]);

        setProducts(currProducts);
    }, [repayments]);

    useEffect(() => {
        if (financeData.errorMessage !== '') {
            setShowInsufficientPower(true);
            if (financeData.estimation.length > 0) {
                setInsufficientBorrowingAmount(Number(financeData.estimation[0].borrowAmount));
                setInSufficentInterestParas([
                    getProductInterestTitle(financeData.estimation[0].loanType),
                    `${formatCurrency(financeData.estimation[0].interestRate, true).toString()}`,
                    `${formatCurrency(financeData.estimation[0].comparisionRate, true).toString()}`]);
            }

            return;
        }
        let newEstimation = financeData.estimation.map((e) => ({
            ...e,
            loanTypeId: getProductID(e.loanType),
        }));

        if (isNotCarLoan) {
            newEstimation = newEstimation.filter((item) => item.loanTypeId !== LOAN_TYPE_ID.SECURED);
        }
        const arr = [...newEstimation].sort((a, b) => b.loanTypeId - a.loanTypeId);

        setMaxAmount(arr[0].borrowAmount);
        setBorrowingAmount(arr[0].borrowAmount);

        const loanTypeName = getProductInterestTitle(arr[0].loanType);

        dispatch(updateLoanTypeName(loanTypeName));

        setInSufficentInterestParas([
            loanTypeName,
            `${formatCurrency(arr[0].interestRate, true).toString()}`,
            `${formatCurrency(arr[0].comparisionRate, true).toString()}`]);

        setInterestParas([
            `${formatCurrency(arr[0].interestRate, true).toString()}`,
            `${formatCurrency(arr[0].comparisionRate, true).toString()}`]);

        setLoanTerms({ label: loanYearsForm.DropdownOptions[0].key, value: loanYearsForm.DropdownOptions[0].value });
        dispatch(updateLoanTerm(Number(loanYearsForm.DropdownOptions[0].value)));
        calculateCappedFees();
    }, [financeData]);

    return (
        <fieldset>
            <legend>
                <h2 id="borrowing-power-main-heading" className="h1" tabIndex={-1} ref={headingRef}>
                    <DynamicContent content={actualHeading} id="borrowing-power-main-heading-content" />
                </h2>
            </legend>
            <DynamicContent
                content={description}
                id="borrowing-power-main-description"
                className="mb-3"
                isParagraph
            />
            {showInsufficientPower && (
                <InSufficientPower
                    minimumLoanAmount={loanConfiguration.minimumLoanAmount}
                    insufficientAmount={insufficientBorrowingAmount}
                    description={productType === 'Standard' ? interestDescription : greenDescription}
                    descriptionParas={inSufficentInterestParas}
                >
                    {showInsufficientPowerDropdown && (
                        <DropdownComponentWrapper className="mb-2">
                            <h3 id="borrowing-power-sub-heading" className="h3">
                                {loanTermsDropDownLabel}
                            </h3>
                            <DropdownWrapper>
                                <Dropdown
                                    style={{ fontSize: '15px' }}
                                    defaultOption="Select"
                                    aria-labelledby="borrowing-power-sub-heading loan-term-year-dropdown"
                                    id="loan-term-year-dropdown"
                                    name="loan-term-year-dropdown"
                                    label=""
                                    isValid
                                    options={loanYearsForm.DropdownOptions.map((o) => ({ label: o.key, value: o.value }))}
                                    value={loanTerms.value}
                                    onChange={handleYearsChange}
                                    hideDefaultOption
                                    aria-label="Select loan term in years"
                                />
                            </DropdownWrapper>
                        </DropdownComponentWrapper>
                    )}
                </InSufficientPower>
            )}
            {!showInsufficientPower
            && (
                <>
                    <AmountSlider
                        minValue={loanConfiguration.minimumLoanAmount}
                        maxValue={maxAmount}
                        currentValue={borrowingAmount}
                        header={borrowingCapacityLabel}
                        description={productType === 'Standard' ? normalInterest : greenInterest}
                        descriptionParas={interestParas}
                        onChange={handleAmountChange}
                        onErrorHandle={onSliderError}
                        forwardedRef={slideInputRef}
                    />
                    <DropdownComponentWrapper>
                        <h3 id="borrowing-power-sub-heading" className="h3">
                            {loanTermsDropDownLabel}
                        </h3>
                        <DropdownWrapper>
                            <Dropdown
                                style={{ fontSize: '15px' }}
                                defaultOption="Select"
                                aria-labelledby="borrowing-power-sub-heading loan-term-year-dropdown"
                                id="loan-term-year-dropdown"
                                name="loan-term-year-dropdown"
                                label=""
                                isValid
                                options={loanYearsForm.DropdownOptions.map((o) => ({ label: o.key, value: o.value }))}
                                value={loanTerms.value}
                                onChange={handleYearsChange}
                                hideDefaultOption
                            />
                        </DropdownWrapper>
                    </DropdownComponentWrapper>
                    <DynamicWidthWrapper isDynamicWidth={isNotCarLoan}>
                        <CarouselControl
                            className="py-1 carousel-control-wrapper"
                            activeSlide={0}
                            id="loan-option-carousel-control"
                            pageSize={{ md: 2, lg: 3, xl: 3 }}
                        >
                            {products.map((product) => (
                                <LoanPresentation
                                    isRecommendedPrimary={product.isRecommendedPrimary}
                                    isRecommendedPrimaryText={product.isRecommendedPrimaryText}
                                    isRecommendedSecondary={product.isRecommendedSecondary}
                                    isRecommendedSecondaryText={product.isRecommendedSecondaryText}
                                    isRecommendedOffer={product.isRecommendedOffer}
                                    id={product.id}
                                    cappedMessage={product.cappedMessage}
                                    heading={product.heading}
                                    repayment={product.repayment}
                                    offer={product.offer}
                                    dynamicMessageInfo={product.dynamicMessageInfo}
                                    params={product.params}
                                    key={product.id}
                                    collapsed={isHiddenAllDynamicMessages}
                                    hyperLinkText={product.hyperLinkText}
                                    hyperOpenLink={product.hyperOpenLink}
                                />
                            ))}
                        </CarouselControl>
                    </DynamicWidthWrapper>
                </>
            )}
            {!showInsufficientPower && (
                <DynamicContent
                    content={offerInfo}
                    id="borrowing-power-offer-description"
                    className="mb-2"
                    isParagraph
                />
            )}
            {!showInsufficientPower && productType === 'Standard' && (
                <DynamicContent
                    content={representativeRateInfo}
                    id="borrowing-power-bottom-description"
                    className="mb-3"
                    isParagraph
                />
            )}
            {!showInsufficientPower && (
                <ButtonActionEmphasis
                    className="xs-12"
                    id="borrowing-power-next-button"
                    data-testid="borrowing-power-next-button"
                    variant="emphasis"
                    onClick={handleNextButton}
                >
                    {exploreLoanOptionsButtonText}
                </ButtonActionEmphasis>
            )}
            <InsufficientPowerButtonWrapper>
                <ButtonActionSubtle
                    className="xs-12"
                    id="borrowing-power-reset-button"
                    data-testid="borrowing-power-reset-button"
                    onClick={handleResetButton}
                >
                    {resetEstimateText}
                </ButtonActionSubtle>
            </InsufficientPowerButtonWrapper>
        </fieldset>
    );
};

export default BorrowingPower;
